import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Input, TextArea } from "../../../../../common/Input";
import { ConfirmationModal } from "../../../../../common/Modal";

export function SalaryAdvanceSubmitModal({
  isOpen,
  onClose,
  selectedEmployee,   // pre-filled from the table row that was clicked
  onSubmit,
  loadingRequest,
}) {
  const [requestedDate, setRequestedDate] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [errorState, setErrorState] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false); // ADDED

  const handleClose = () => {
    setRequestedDate("");
    setAmount("");
    setReason("");
    setErrorState(false);
    setShowConfirm(false);
    onClose();
  };

  // MODIFIED: Only handles validation and showing the confirmation
  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (!requestedDate || !amount || parseFloat(amount) <= 0) {
      setErrorState(true);
      return;
    }
    setErrorState(false);
    setShowConfirm(true);
  };

  // ADDED: Handles the actual API call after confirmation
  const handleConfirmSubmit = async () => {
    const result = await onSubmit({
      employeePersonalInformationId: selectedEmployee?.employeePersonalInformationId,
      requestedDate,
      amount: parseFloat(amount),
      reason,
    });

    if (result?.success) {
      handleClose();
    } else {
      setShowConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[80%] lg:w-[45%] xl:w-[35%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <PostAddIcon className="text-customMainColor" fontSize="medium" />
              <span className="text-xl text-customMainColor">New Salary Advance Request</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer"
              aria-label="Close modal"
              disabled={loadingRequest}
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">

            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4 pt-2">
              <div className="py-1 border-b border-gray-300 mb-4">
                <h3 className="font-medium text-gray-700 uppercase text-xs tracking-widest">Application Details</h3>
              </div>

              <div className="space-y-4">
                {/* Employee — read-only using Input component */}
                <Input
                  label="Employee Name"
                  value={selectedEmployee?.employeeFullName ?? ""}
                  isViewOnly={true}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                  <Input
                    label="Requested Date"
                    type="date"
                    isRequired={true}
                    value={requestedDate}
                    onChange={(e) => setRequestedDate(e.target.value)}
                    error={errorState && !requestedDate}
                    errorMessage="Please fill up this field"
                  />

                  <Input
                    label="Requested Amount"
                    type="number"
                    isRequired={true}
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    error={errorState && (!amount || parseFloat(amount) <= 0)}
                    errorMessage="Please enter a valid amount"
                  />
                </div>

                <TextArea
                  label="Reason for Request"
                  placeholder="State your reason here..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
            <button
              onClick={handleClose}
              disabled={loadingRequest}
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors font-medium"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmitClick}
              disabled={loadingRequest}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded border-none cursor-pointer font-bold transition-colors"
            >
              {loadingRequest ? "Submitting..." : "Submit Request"}
            </button>
          </div>

        </div>
      </div>

      {/* Confirmation Modal - ADDED */}
      <ConfirmationModal
        visible={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmSubmit}
        title="Confirm Submission"
        message={`Are you sure you want to submit a salary advance request of ₱${Number(amount || 0).toLocaleString()} for ${selectedEmployee?.employeeFullName}?`}
        confirmText="Confirm"
        cancelText="Cancel"
        type="info"
        isLoading={loadingRequest}
      />
    </>
  );
}